// Client-side list sorting, shared by the sidebar tree (display order) and the
// Navigator (arrow-key nav + advance-in-source), so every consumer of a list
// sees the same order. Sort state persists in localStorage.

import { RATING_LABELS, type ListItem } from "./types";

export type SortField = "title" | "year" | "rating";
export type SortDir = "asc" | "desc";
export interface SortState {
  field: SortField;
  dir: SortDir;
}

export const SORT_FIELDS: SortField[] = ["title", "year", "rating"];
export const SORT_FIELD_LABEL: Record<SortField, string> = {
  title: "A–Z",
  year: "year",
  rating: "rating",
};

const STORAGE_KEY = "cinedb.sort";
const DEFAULT: SortState = { field: "title", dir: "asc" };

export function loadSort(): SortState {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as SortState;
      if (SORT_FIELDS.includes(p.field) && (p.dir === "asc" || p.dir === "desc")) return p;
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT;
}

export function saveSort(s: SortState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function cycleField(field: SortField): SortField {
  return SORT_FIELDS[(SORT_FIELDS.indexOf(field) + 1) % SORT_FIELDS.length];
}

const ARTICLE = /^(the|a|an)\s+/i;
const titleKey = (t: string): string => t.replace(ARTICLE, "").toLowerCase();

// ── Filtering — options are contextual to the active sort field ──────────────
export interface FilterState {
  field: SortField;
  value: string;
}
export interface FilterOption {
  value: string;
  label: string;
}

const firstLetter = (t: string): string => {
  const c = t.replace(ARTICLE, "").trimStart().charAt(0).toUpperCase();
  return c >= "A" && c <= "Z" ? c : "#";
};

/** The dropdown choices for the current sort field. */
export function filterOptions(field: SortField): FilterOption[] {
  if (field === "title") {
    return [
      ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((c) => ({ value: c, label: c })),
      { value: "#", label: "# (other)" },
    ];
  }
  if (field === "year") {
    const top = Math.floor(new Date().getFullYear() / 10) * 10;
    const opts: FilterOption[] = [];
    for (let d = top; d >= 1900; d -= 10) opts.push({ value: String(d), label: `${d}s` });
    return opts;
  }
  return [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
    value: String(n),
    label: `${n} · ${RATING_LABELS[n]}`,
  }));
}

export function filterLabel(filter: FilterState): string {
  return filterOptions(filter.field).find((o) => o.value === filter.value)?.label ?? filter.value;
}

function matchesFilter(item: ListItem, f: FilterState): boolean {
  if (f.field === "title") return firstLetter(item.title) === f.value;
  if (f.field === "year") {
    return item.year != null && Math.floor(item.year / 10) * 10 === Number(f.value);
  }
  return item.rating != null && item.rating === Number(f.value);
}

/** Filter (if set) then sort — the single order every consumer renders. */
export function viewItems(
  items: ListItem[],
  sort: SortState,
  filter: FilterState | null,
): ListItem[] {
  const base = filter ? items.filter((i) => matchesFilter(i, filter)) : items;
  return sortItems(base, sort);
}

/** A stably-sorted copy of `items`. Missing year/rating always sort last. */
export function sortItems(items: ListItem[], { field, dir }: SortState): ListItem[] {
  const mult = dir === "asc" ? 1 : -1;
  const byTitle = (a: ListItem, b: ListItem) => titleKey(a.title).localeCompare(titleKey(b.title));
  return [...items].sort((a, b) => {
    if (field === "title") return byTitle(a, b) * mult;
    const av = field === "year" ? a.year : a.rating;
    const bv = field === "year" ? b.year : b.rating;
    if (av == null && bv == null) return byTitle(a, b);
    if (av == null) return 1; // nulls last, regardless of direction
    if (bv == null) return -1;
    if (av !== bv) return (av - bv) * mult;
    return byTitle(a, b); // tie-break alphabetically
  });
}
